/**
 * icons.js - Set de iconos SVG inline, monocromos y coherentes (estilo "outline" tipo Feather),
 * usados en el panel de UI. Usan `currentColor`, así que heredan el color del texto del botón y
 * se ven igual en cualquier sistema operativo (a diferencia de los emoji).
 *
 * Uso: `svgIcon('play')` devuelve la cadena `<svg>…</svg>` lista para insertar en innerHTML.
 */

// Cada icono: cuerpo SVG (24x24) y si es de relleno (`filled`) o de trazo (por defecto).
const ICONS = {
    // Controles de simulación
    play: { filled: true, body: '<polygon points="6 4 20 12 6 20 6 4"/>' },
    pause: {
        filled: true,
        body: '<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>',
    },
    reset: {
        body: '<polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>',
    },
    trash: {
        body: '<polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/>',
    },

    // Cámara / seguimiento
    sun: {
        body: '<circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>',
    },
    eye: {
        body: '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
    },
    globe: {
        body: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
    },
    target: { body: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3"/>' },
    edgeOn: { body: '<line x1="2" y1="12" x2="22" y2="12"/><circle cx="12" cy="12" r="2.5"/>' },
    maximize: {
        body: '<path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>',
    },

    // Generales / pestañas
    settings: {
        body: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>',
    },
    close: { body: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>' },
    book: {
        body: '<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>',
    },
    star: {
        filled: true,
        body: '<polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>',
    },
    aperture: {
        body: '<circle cx="12" cy="12" r="10"/><line x1="14.31" y1="8" x2="20.05" y2="17.94"/><line x1="9.69" y1="8" x2="3.95" y2="17.94"/><line x1="7.38" y1="12" x2="13.12" y2="2.06"/><line x1="16.62" y1="12" x2="10.88" y2="2.06"/><line x1="3.95" y1="6.06" x2="9.69" y2="16"/><line x1="20.05" y1="6.06" x2="14.31" y2="16"/>',
    },
    navigation: { filled: true, body: '<polygon points="3 11 22 2 13 21 11 13 3 11"/>' },
    // Heliosfera: dos círculos concéntricos (choque de terminación + heliopausa)
    heliosphere: {
        body: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5" opacity="0.6"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>',
    },
};

/**
 * Devuelve el SVG de un icono como cadena lista para innerHTML.
 * @param {string} name - Nombre del icono (clave de ICONS)
 * @param {number} [size=18] - Tamaño en px
 * @returns {string} Markup SVG, o cadena vacía si el icono no existe
 */
export function svgIcon(name, size = 18) {
    const def = ICONS[name];
    if (!def) return '';
    const fill = def.filled ? 'currentColor' : 'none';
    const stroke = def.filled ? 'none' : 'currentColor';
    return `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${def.body}</svg>`;
}

/**
 * Devuelve un icono de planeta: un disco del color real del planeta (Saturno con anillo).
 * @param {string} color - Color CSS, p.ej. '#6B93D6'
 * @param {boolean} [ring=false] - Dibujar anillo (para Saturno)
 * @param {number} [size=16] - Tamaño en px
 * @returns {string} Markup SVG
 */
export function planetIcon(color, ring = false, size = 16) {
    const ringMarkup = ring
        ? `<ellipse cx="12" cy="12" rx="10.5" ry="3.6" fill="none" stroke="${color}" stroke-width="1.6" opacity="0.85" transform="rotate(-20 12 12)"/>`
        : '';
    return `<svg class="icon" width="${size}" height="${size}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="6.5" fill="${color}"/>${ringMarkup}</svg>`;
}
